import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Check, Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { submitEnquiry } from "../lib/api";
import { CONTACT, SERVICES } from "../data/content";
import { useBooking } from "../context/BookingContext";
import { IMAGES } from "../data/content";

const empty = { name: "", email: "", phone: "", child_age: "", service: "", region: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const { openBooking } = useBooking();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await submitEnquiry({ ...form, type: "contact" });
      setStatus("done");
      setForm(empty);
    } catch {
      setStatus("error");
    }
  };

  const field = "w-full rounded-[16px] bg-sora-bg border border-sora-stone/50 px-4 py-3 text-sora-charcoal placeholder:text-sora-charcoal/40 focus:outline-none focus:border-sora-blue focus:ring-2 focus:ring-sora-blue/20 transition";

  return (
    <div data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        eyebrow="Contact"
        title="Let's start a conversation."
        subtitle="Have a question or ready to book? Reach out — Alicia personally replies to every enquiry, usually within one business day."
        image={IMAGES.interior}
      />

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_0.85fr] gap-10">
          {/* Form */}
          <Reveal>
            <div className="rounded-[28px] bg-sora-warm border border-sora-stone/25 p-8 md:p-10 shadow-[0_8px_30px_rgba(63,88,115,0.05)]">
              {status === "done" ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <span className="mx-auto mb-5 grid place-items-center h-16 w-16 rounded-full bg-sora-sage/50 text-sora-slate"><Check size={30} strokeWidth={1.5} /></span>
                  <h3 className="font-display text-2xl text-sora-slate mb-2">Message sent</h3>
                  <p className="text-sora-charcoal/75 leading-relaxed max-w-sm mx-auto">Thank you for reaching out. We'll be in touch personally very soon.</p>
                  <button onClick={() => setStatus("idle")} className="mt-6 text-sora-blue font-bold">Send another message</button>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-2xl md:text-3xl text-sora-slate mb-1">Send us a message</h2>
                  <p className="text-sm text-sora-charcoal/70 mb-6">We'd love to hear about your child.</p>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="contact-name" required placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
                      <input data-testid="contact-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="contact-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
                      <input data-testid="contact-child-age" placeholder="Child's age" value={form.child_age} onChange={set("child_age")} className={field} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <select data-testid="contact-service" value={form.service} onChange={set("service")} className={field}>
                        <option value="">Area of support</option>
                        {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                      <input data-testid="contact-region" placeholder="Your suburb" value={form.region} onChange={set("region")} className={field} />
                    </div>
                    <textarea data-testid="contact-message" rows={4} placeholder="How can we help?" value={form.message} onChange={set("message")} className={field} />
                    {status === "error" && <p data-testid="contact-error" className="text-sm text-red-600">Something went wrong. Please try again or email us directly.</p>}
                    <button data-testid="contact-submit" disabled={status === "loading"} className="inline-flex items-center justify-center gap-2 bg-sora-blue text-white rounded-[18px] px-8 py-3.5 font-bold hover:bg-sora-slate transition disabled:opacity-70">
                      {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send message"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </Reveal>

          {/* Details */}
          <Reveal delay={0.1}>
            <div className="space-y-5">
              <div className="rounded-[24px] bg-sora-warm border border-sora-stone/25 p-7">
                <h3 className="font-display text-xl text-sora-slate mb-4">Business details</h3>
                <ul className="space-y-3.5 text-sora-charcoal/80">
                  <li><a href={`mailto:${CONTACT.email}`} data-testid="contact-detail-email" className="flex items-center gap-3 hover:text-sora-blue transition"><span className="grid place-items-center h-10 w-10 rounded-full bg-sora-blue/12 text-sora-blue"><Mail size={18} strokeWidth={1.5} /></span>{CONTACT.email}</a></li>
                  <li><a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-detail-phone" className="flex items-center gap-3 hover:text-sora-blue transition"><span className="grid place-items-center h-10 w-10 rounded-full bg-sora-blue/12 text-sora-blue"><Phone size={18} strokeWidth={1.5} /></span>{CONTACT.phone}</a></li>
                  <li className="flex items-center gap-3"><span className="grid place-items-center h-10 w-10 rounded-full bg-sora-blue/12 text-sora-blue"><MapPin size={18} strokeWidth={1.5} /></span>Mobile — we come to you</li>
                </ul>
                <button data-testid="contact-book-now" onClick={() => openBooking("booking")} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-sora-blue text-white rounded-[18px] px-6 py-3.5 font-bold hover:bg-sora-slate transition">
                  Book an Appointment <ArrowRight size={18} />
                </button>
              </div>

              <div className="rounded-[24px] bg-sora-warm border border-sora-stone/25 p-7">
                <h3 className="font-display text-xl text-sora-slate mb-4 flex items-center gap-2"><Clock size={18} className="text-sora-blue" strokeWidth={1.5} /> Opening hours</h3>
                <ul className="space-y-2.5">
                  {CONTACT.hours.map((h) => (
                    <li key={h.day} className="flex justify-between text-sm"><span className="text-sora-charcoal/70">{h.day}</span><span className="text-sora-slate font-medium">{h.time}</span></li>
                  ))}
                </ul>
              </div>

              <div className="rounded-[24px] bg-sora-sage/25 border border-sora-stone/25 p-7">
                <h3 className="font-display text-xl text-sora-slate mb-1">Service areas</h3>
                <p className="text-sm text-sora-charcoal/70 mb-3">Servicing most of metropolitan Melbourne, including:</p>
                <div className="flex flex-wrap gap-2">
                  {CONTACT.regions.map((r) => (
                    <span key={r} className="rounded-full bg-sora-warm px-4 py-1.5 text-sm text-sora-slate border border-sora-stone/40">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-[28px] overflow-hidden border border-sora-stone/25 shadow-[0_8px_30px_rgba(63,88,115,0.06)]">
              <iframe
                title="Melbourne service area"
                data-testid="contact-map"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d403188.3!2d144.9!3d-37.84!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sau!4v1700000000000"
                width="100%" height="420" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
